import { Box, Flex, Grid, Image, Text, Tooltip } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React from 'react';

import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import IconSvg from 'ui/shared/IconSvg';

type Source = {
  _id: {
    $oid: string;
  };
  address: string;
  client: string;
  confirmed_task_count: number;
  createdAt: string;
  image_content: {
    cpu?: string;
    nvidia?: string;
    amd?: string;
    name: string;
    description: string;
    logo_url: string;
    tags: Array<string>;
  };
  image_metadata_url: string;
  last_updated_at: number;
  registered_provider_count: number;
  responded_task_count: number;
  source_constraints: {
    delivery_method: number;
    image_environments: number;
    max_expiry_duration: number;
    min_available_lockup: string;
    min_payment: string;
    optional_params_url: string;
    privacy_enabled: boolean;
  };
  status: string;
  total_response_time: number;
  total_task_count: number;
  updatedAt: string;
  published_at: number;
  total_offered_reward: number;
  total_payment: number;
};

type Props = {
  source: Source;
  isLoading?: boolean;
};

const LatestSourceItem = ({ source, isLoading }: Props) => {
  return (
    <Box
      as={ motion.div }
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ display: 'none' }}
      transitionDuration="normal"
      transitionTimingFunction="linear"
      borderRadius="md"
      border="1px solid"
      borderColor="divider"
      p={ 2 }
      height="100%"
      maxWidth="100%"
      fontSize="sm"
    >
      <Flex alignItems="center" overflow="hidden" w="100%" mb={ 2 }>
        { source.image_content?.logo_url ? (
          <Image
            src={ source.image_content.logo_url }
            alt={ source.image_content.name }
            boxSize="24px"
            borderRadius="md"
            mr={ 2 }
            objectFit="cover"
          />
        ) : (
          <IconSvg
            name="block_slim"
            boxSize={ 5 }
            mr={ 2 }
            p="1px"
            isLoading={ isLoading }
            flexShrink={ 0 }
          />
        ) }
        <Tooltip label={ source.image_content?.name || 'Unknown' }>
          <Text
            fontSize="sm"
            fontWeight={ 500 }
            mr="auto"
            maxW="160px"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            { source.image_content?.name || 'Unknown' }
          </Text>
        </Tooltip>
        { source.status === 'Active' && (
          <Tooltip label="Active Source">
            <IconSvg
              name="status/success"
              boxSize={ 4 }
              p="1px"
              ml={ 2 }
              isLoading={ isLoading }
              flexShrink={ 0 }
            />
          </Tooltip>
        ) }
      </Flex>
      <Grid gridGap={ 1 } templateColumns="auto minmax(0, 1fr)" fontSize="xs">
        <Text>Source</Text>
        <Box maxW="100%" overflow="hidden">
          <AddressEntity
            address={{ hash: source.address }}
            isLoading={ isLoading }
            noIcon
            noCopy
            truncation="constant"
          />
        </Box>

        <Text>Providers</Text>
        <Text color="text_secondary">
          { source.registered_provider_count }
        </Text>

        <Text>Tasks</Text>
        <Text color="text_secondary">{ source.total_task_count }</Text>
      </Grid>
    </Box>
  );
};

export default LatestSourceItem;
